import React, { useState, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useMultipleFileUpload, useNotification } from "@hooks/index";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { isEqual } from "lodash";
import { useForm, Controller, FormProvider } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import FormSimple from "@components/formGroup/formSimple";
import FormTextarea from "@components/formGroup/formTextarea";
import InputFileMultipleArh from "@components/input/InputFileMultiple";
import TaskExpectedResult from "@components/onboarding/TaskExpectedResult";
import ListBoxDocuments from "@components/tailwindComponents/ListBoxDocuments";
import {
  Switch,
  ListboxEmploye,
  ListboxSimple
} from "@components/tailwindComponents";

import { TASK_EXPECTED_RESULT_LIST } from "../../onboarding.config";
import {
  REGEX_VALID_FILES,
  defaultDocuments,
  defaultDocumentsRH
} from "@helpers/_constants";
import {
  formatDateToDateString,
  formatDateStringToDate
} from "@helpers/_functions";
import { convertToFormData } from "@helpers/_functions";
import { getFileTitle } from "@helpers/_functions";
import { TaskStatus } from "@helpers/moduleConstants/taskConstants";
import { OnboardingEmployeTask, DocumentStorage } from "@services/all.services";
import { NotificationUtil } from "@utils/notification.utils";

import DateManagement, { DateType } from "./DateManagement";

const WITH_UPLOAD_TASK_TYPES = [
  "document_sending",
  "signature_request",
  "electronic_signature"
];
const WITH_FOLDER_TASK_TYPE = [
  "document_sending",
  "upload_request",
  "signature_request",
  "electronic_signature"
];

const PDF_FILE_TYPE = ".pdf,.PDF";

const LANGUAGE_LIST = [
  { _id: "fr", name: "Francais" },
  { _id: "en", name: "Anglais" }
];

function populateAssignedTo(assignedTo, employeList) {
  return assignedTo.map((employeAssigned) =>
    employeList.find((employe) => employe.id === employeAssigned._id)
  );
}

function getTargetFolder(folderId, folderList = [], docType) {
  if (folderId) {
    return folderList.find((folder) => folder._id === folderId) || null;
  }

  if (docType) {
    return docType === "documentRH" ? defaultDocumentsRH[0] : defaultDocuments[0];
  }

  return null;
}

/**
 *
 * @param {Object} props
 * @param {Object} props.selectedTask
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {any[]=} props.employeFolders
 * @param {any[]} props.taskList
 * @returns
 */
const ModalManagedTask = (props) => {
  const { t } = useTranslation("beginEnd.protocol");
  const { showError, showSuccess, showInfo } = useNotification();
  const {
    oldFiles,
    handleDeleteOldFiles,
    removeOldFiles,
    handleUploadStatusChange,
    resetInputFiles,
    status: uploadStatus
  } = useMultipleFileUpload({ defaultFiles: props.selectedTask?.files });

  const methods = useForm();
  const { handleSubmit, control, reset, watch, setValue } = methods;

  const expectedResultType = watch("expectedResultType");

  const employerList = useReduxSelector(
    (state) => state.evaluation.eligibleEvaluator.list
  );

  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (props.isOpen && props?.selectedTask) {
      const expectedResultType = TASK_EXPECTED_RESULT_LIST.find(
        (t) => t._id === props.selectedTask.expectedResultType
      );
      const targetFolder = getTargetFolder(
        props.selectedTask.targetFolder,
        props.employeFolders,
        props.selectedTask.docType
      );

      reset({
        ...props.selectedTask,
        expectedResultType,
        assignedTo: populateAssignedTo(
          props.selectedTask.assignedTo,
          employerList
        ),
        targetFolder,
        treeDocs: targetFolder ? [targetFolder._id || targetFolder.value] : [],
        beneficiary: null,
        documentLanguage:
          props.selectedTask.documentLanguage &&
          LANGUAGE_LIST.find(
            (e) => e._id === props.selectedTask.documentLanguage
          ),

        predictedDate: formatDateStringToDate(
          props.selectedTask?.predictedDate
        ),
        deadlineDate: formatDateStringToDate(props.selectedTask?.deadlineDate),
        dateType: props.selectedTask.predictedDateTriggerTask
          ? DateType.FOLLOWING_TASK
          : DateType.FIXED_DATE,
        predictedDateTriggerTask: props.taskList.find(
          (t) => t._id === props.selectedTask.predictedDateTriggerTask
        ),
        deadlineDateTriggerTask: props.taskList.find(
          (t) => t._id === props.selectedTask.deadlineDateTriggerTask
        ),
        predictedDateTriggerInterval:
          props.selectedTask.predictedDateTriggerInterval?.toString() || "1",
        deadlineDateTriggerInterval:
          props.selectedTask.deadlineDateTriggerInterval?.toString() || "1"
      });
      resetInputFiles();
      setIsSaving(false);
    }
  }, [
    props.isOpen,
    props.selectedTask,
    employerList,
    props.employeFolders,
    props.taskList
  ]);

  useEffect(() => {
    resetInputFiles();
    setValue("newFiles", []);
  }, [expectedResultType]);

  async function handleSubmitSuccess(formValue) {
    try {
      setIsSaving(true);
      const taskId = props.selectedTask._id;
      let {
        files,
        newFiles,
        assignedTo,
        expectedResultType,
        targetFolder,
        treeDocs,
        beneficiary,
        documentLanguage,
        dateType,
        predictedDate,
        deadlineDate,
        predictedDateTriggerInterval,
        predictedDateTriggerTask,
        deadlineDateTriggerInterval,
        deadlineDateTriggerTask,
        ...requestData
      } = formValue;

      if (!isEqual(uploadStatus, "done") && newFiles?.length) {
        return showInfo("please_wait_uploading");
      }

      if (WITH_FOLDER_TASK_TYPE.includes(expectedResultType._id) && targetFolder) {
        if (targetFolder._id) {
          requestData.targetFolder = targetFolder._id;
        }
        requestData.docType = targetFolder.docType || "document";
      }

      let dateValue = {};
      if (dateType === DateType.FIXED_DATE) {
        dateValue = {
          predictedDate: formatDateToDateString(formValue.predictedDate),
          deadlineDate: formatDateToDateString(formValue.deadlineDate),
          predictedDateTriggerInterval: 0,
          predictedDateTriggerTask: null,
          deadlineDateTriggerInterval: 0,
          deadlineDateTriggerTask: null
        };
      } else {
        dateValue = {
          predictedDate: "",
          deadlineDate: "",
          predictedDateTriggerInterval: +predictedDateTriggerInterval || 0,
          predictedDateTriggerTask: predictedDateTriggerTask?._id,
          deadlineDateTriggerInterval: +deadlineDateTriggerInterval || 0,
          deadlineDateTriggerTask: deadlineDateTriggerTask?._id
        };
      }

      requestData = {
        ...requestData,
        ...dateValue,
        files: newFiles,
        expectedResultType: expectedResultType._id,
        assignedTo: assignedTo.map((e) => e.id),
        sourceModel: "OnboardingEmployeTask",
        documentLanguage: documentLanguage?._id
      };

      try {
        await removeOldFiles();

        const newTasks = await OnboardingEmployeTask.updateTask(
          taskId,
          convertToFormData(requestData)
        );

        if (
          WITH_FOLDER_TASK_TYPE.includes(expectedResultType._id) &&
          formValue?.targetFolder
        ) {
          await uploadAnswerFiles(newTasks);
        }

        // send notification
        if (newTasks?.assignedTo?.length && newTasks.predictedDate) {
          await NotificationUtil.onboardingEmployeTask(newTasks);
        }
      } catch (error) {
        console.log("Error when uploading files answer: ", error);
      }

      if (
        !WITH_FOLDER_TASK_TYPE.includes(expectedResultType._id) &&
        formValue?.targetFolder?._id
      ) {
        await showSuccess("assignation_action_success", {
          object: "aProtocol"
        });
      }
      await props.onClose();
    } catch (err) {
      console.error(err);
      showError("error");
      setIsSaving(false);
    }
  }

  function handleSubmitError(error) {
    showError("field_obligatory");
  }

  async function uploadAnswerFiles(newTasks) {
    let { files, beneficiary, Enterprise, targetFolder } = newTasks;
    const uploadedFiles =
      files?.concat(props.selectedTask.files) ||
      props?.selectedTask?.files ||
      [];
    if (!uploadedFiles.length) {
      return;
    }

    const storageResponse = targetFolder
      ? await DocumentStorage.getByDocumentId(targetFolder)
      : null;
    const targetStorage = storageResponse?.data?.data?.[0];
    if (uploadedFiles?.length) {
      for (let datafile of uploadedFiles) {
        let dataFolder = {
          key: (targetStorage?.key || "/") + getFileTitle(datafile.key),
          originalName: datafile.filename || getFileTitle(datafile.key),
          wasabiName: getFileTitle(datafile.key),
          type: "file",
          isReadWrite: false,
          isVisible: false,
          employerId: beneficiary,
          entrepriseId: Enterprise,
          datafile: datafile._id,
          docType: targetStorage?.docType ?? newTasks.docType ?? "document"
        };
        await DocumentStorage.insert(dataFolder);
      }

      await showSuccess("assignation_action_success", { object: "aProtocol" });
      await props.onClose();
    }
  }

  const status = watch("status");

  const requiredRule = useMemo(() => {
    return {
      validate: {
        required: (value) => {
          if (status === TaskStatus.NOT_APPLICABLE) {
            return true;
          }

          return !!value;
        }
      }
    };
  }, [status]);

  return (
    <ModalIndex
      isOpen={props.isOpen}
      onClickClose={props.onClose}
      title={t("Assigner une tâche")}
      // buttonType="none"
      buttonType="double"
      isLoading={isSaving}
      buttonOptions={{
        title: t("Enregistrer"),
        color: "green",
        click: handleSubmit(handleSubmitSuccess, handleSubmitError)
      }}
      buttonCancelOptions={{
        title: t("Annuler"),
        color: "outlined",
        click: props.onClose
      }}
    >
      <>
        <div className="arh-mx-auto arh-my-[1rem]">
          <TaskExpectedResult type={expectedResultType?._id} />
        </div>
        <div className="arh-flex arh-items-center arh-justify-center arh-space-x-2">
          <Controller
            control={control}
            name="status"
            defaultValue={TaskStatus.TODO}
            render={({ field: { onChange, value } }) => (
              <Switch
                checked={value === TaskStatus.NOT_APPLICABLE}
                handleChange={() => {
                  if (value === TaskStatus.NOT_APPLICABLE) {
                    onChange(TaskStatus.TODO);
                  } else {
                    onChange(TaskStatus.NOT_APPLICABLE);
                  }
                }}
                color="arh-bg-greenColor"
              />
            )}
          />

          <span className="arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor ">
            {t("Non applicable")}
          </span>
        </div>

        <div className="arh-my-[1.5rem] arh-flex arh-flex-col arh-justify-center arh-space-y-3">
          <Controller
            control={control}
            name="assignedTo"
            defaultValue={[]}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <ListboxEmploye
                label={t("Assigné à")}
                placeholder={t("Employé")}
                name={name}
                optionList={employerList}
                selectedValue={value}
                setSelectedValue={(newValue) => {
                  onChange(newValue);
                }}
                //invalid={invalid}
                multiple
              />
            )}
          />

          <Controller
            control={control}
            name="name"
            defaultValue=""
            rules={requiredRule}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { invalid }
            }) => (
              <FormSimple
                label={t("Tâche à réaliser")}
                placeholder="Nom du tâche"
                ParentForm="arh-mb-[0.938rem]"
                classLabel="arh-flex"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                invalid={invalid}
              />
            )}
          />

          <Controller
            control={control}
            name="detail"
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <FormTextarea
                label={t("Détails/consignes")}
                placeholder="Détails/consignes"
                ParentForm="arh-mb-[0.938rem]"
                classTextarea="arh-resize-none arh-h-[5.625rem] arh-max-h-[5.625rem]"
                classLabel="arh-flex"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          <div className="arh-mb-[0.938rem] arh-w-full">
            <label className="arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
              {t("Résultat attendu")}
            </label>
            <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
              <Controller
                control={control}
                name="expectedResultType"
                defaultValue=""
                rules={requiredRule}
                render={({
                  field: { onChange, value },
                  fieldState: { invalid }
                }) => (
                  <ListboxSimple
                    placeholder={t("Résultat attendu")}
                    name={"expectedResultType"}
                    optionList={TASK_EXPECTED_RESULT_LIST?.filter(
                      (type) => type._id !== "request_job"
                    )}
                    selectedValue={value}
                    setSelectedValue={onChange}
                    invalid={invalid}
                    customTranslator={t}
                    // customListHeight="arh-bottom-9"
                  />
                )}
              />
            </div>
          </div>

          {WITH_UPLOAD_TASK_TYPES.includes(expectedResultType?._id) && (
            <Controller
              control={control}
              name="newFiles"
              defaultValue={[]}
              rules={{
                validate: {
                  required: (newFiles) => {
                    if (status === TaskStatus.NOT_APPLICABLE) {
                      return true;
                    }

                    return !!newFiles?.length || !!oldFiles?.length;
                  }
                }
              }}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-w-full">
                  <label
                    className={`arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium ${
                      invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                    }`}
                  >
                    {t("Ajouter le document")}
                  </label>
                  <div className="input_file_theme_employer">
                    <InputFileMultipleArh
                      handleChangeStatus={handleUploadStatusChange(
                        value,
                        onChange
                      )}
                      classUploadFile="arh-upload-createTask employer_theme_upload GreenThemeSelect"
                      regexp={REGEX_VALID_FILES}
                      dataFile={oldFiles} // already uploaded files
                      onDeleteOldFile={handleDeleteOldFiles}
                      isValid={invalid}
                      accept={
                        expectedResultType?._id === "electronic_signature" &&
                        PDF_FILE_TYPE
                      }
                      maxFiles={
                        expectedResultType?._id === "electronic_signature" && 1
                      }
                    />
                  </div>
                </div>
              )}
            />
          )}

          {expectedResultType?._id === "electronic_signature" && (
            <Controller
              control={control}
              name="documentLanguage"
              defaultValue={null}
              rules={requiredRule}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-w-full">
                  <label
                    className={`arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium ${
                      invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                    }`}
                  >
                    {t("Langue du document")}
                  </label>
                  <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                    <ListboxSimple
                      placeholder={t("Langue du document")}
                      name="folderLocation"
                      optionList={LANGUAGE_LIST}
                      selectedValue={value}
                      setSelectedValue={onChange}
                      invalid={invalid}
                      customListHeight="arh-bottom-9"
                      customTranslator={t}
                    />
                  </div>
                </div>
              )}
            />
          )}

          {WITH_FOLDER_TASK_TYPE.includes(expectedResultType?._id) && (
            <Controller
              control={control}
              name="targetFolder"
              defaultValue={null}
              rules={requiredRule}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-w-full">
                  <label
                    className={`${invalid ? "arh-text-invalidColorTextInput" : "arh-text-black"} arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium`}
                  >
                    {t("Placer dans un dossier")}
                  </label>
                  <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                    <ListBoxDocuments
                      placeholder={t("Emplacement du dossier")}
                      control={control}
                      watch={watch}
                      defaultFolders={props.employeFolders}
                      selectedNode={value}
                      setNodeSelected={onChange}
                      invalid={invalid}
                      customWidth="arh-w-full"
                      rules={{}}
                    />
                  </div>
                </div>
              )}
            />
          )}

          {/* Collapse date prévue et date limite */}
          <FormProvider {...methods}>
            <DateManagement
              taskList={props.taskList}
              selectedTask={props.selectedTask}
            />
          </FormProvider>
        </div>
      </>
    </ModalIndex>
  );
};

export default ModalManagedTask;
